/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-confusing-arrow */
/* eslint-disable function-paren-newline */
import Amenities from '@components/HotelCard/Amenities';
import Gallery from '@components/SearchExperiment/Gallery';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import HotelApi from '@customTypes/hotel-api';
import Slide from '@customTypes/slide';
import React, { useEffect, useMemo, useState } from 'react';
import useOnScreen from '@hooks/useOnScreen';
import Image from 'next/image';
import grayHeartIcon from '@assets/images/gray-heart-iconNew.svg';
import redHeartIcon from '@assets/images/red-heart-iconNew.svg';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import AVAILABILITY, {
  PRODUCT_TITLE_CATEGORY_TEXT,
  PRODUCT_CATEGORY_TEXT,
} from '@constants/product';
import Products from '@components/SearchExperiment/HotelCard/Products';
import StarRating from '@components/SearchExperiment/Yotpo/StarRating';

export type HotelCardProps = {
  hotel: HotelApi;
  isHighlighted?: boolean;
  showFavoriteToastMessage: boolean;
  saveToFavorites: boolean;
  userFavorites: any;
  handleAddingHotelIdToFavorites: (hotel: HotelApi) => void;
  selectedVibes: string[];
  productCategory: string;
  href?: string;
  onViewAll?: (hotel: HotelApi) => void;
  onCardView?: (h: HotelApi) => void;
  searchedDate: string;
};

export default function HotelCard({
  hotel,
  isHighlighted = false,
  showFavoriteToastMessage,
  saveToFavorites,
  userFavorites,
  handleAddingHotelIdToFavorites,
  selectedVibes,
  productCategory,
  href = '',
  onViewAll = () => null,
  onCardView = () => {},
  searchedDate = '',
}: HotelCardProps) {
  const [galleryImages, setGalleryImages] = useState<Slide[]>([]);
  type ImageProps = {
    picture: {
      details: { url: string };
      width_375: { url: string };
      url: string;
    };
  };
  useEffect(() => {
    const images = hotel.image.map((image: ImageProps) => ({
      key: image.picture.details.url,
      url: image.picture.details.url,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image.picture.url,
    }));
    const productImages = hotel.product_images?.map((image: string) => ({
      key: image,
      url: image,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image,
    }));
    const hasProductCategory =
      !!PRODUCT_TITLE_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_CATEGORY_TEXT];

    if (hotel.imageDesktop !== images[0]?.urlShort) {
      const filteredImages = images.filter((item) => item.urlShort !== hotel.imageDesktop);
      filteredImages.unshift({
        key: hotel.imageDesktop,
        url: hotel.imageDesktop,
        imgClassnames: 'w-full swiper-lazy rounded-xl',
        urlShort: hotel.imageDesktop,
      });
      if (hasProductCategory && productImages?.length > 0) {
        filteredImages.unshift(...productImages);
      }
      setGalleryImages(filteredImages);
      return;
    }
    if (hasProductCategory && !isHighlighted && productImages?.length > 0) {
      images.unshift(...productImages);
    }
    setGalleryImages(images);
  }, [
    hotel.image,
    hotel.imageDesktop,
    hotel.name,
    hotel.product_images,
    isHighlighted,
    productCategory,
  ]);

  const vibesBadge = useMemo(() => {
    const transformVibe = (vibe: string) => (vibe === 'Family-Friendly' ? 'Family' : vibe);

    if (!hotel.vibes) return '';
    if (!(hotel.vibes.primary || hotel.vibes.secondary)) return '';
    if (!selectedVibes || !selectedVibes.length) {
      return transformVibe(hotel.vibes.primary || hotel.vibes.secondary || '');
    }
    if (
      hotel.vibes.primary &&
      (selectedVibes.includes(hotel.vibes.primary) ||
        selectedVibes.includes(hotel.vibes.primary.toLocaleLowerCase()))
    ) {
      return transformVibe(hotel.vibes.primary);
    }
    if (
      hotel.vibes.secondary &&
      (selectedVibes.includes(hotel.vibes.secondary) ||
        selectedVibes.includes(hotel.vibes.secondary.toLocaleLowerCase()))
    ) {
      return transformVibe(hotel.vibes.secondary);
    }
    return '';
  }, [hotel.vibes, selectedVibes]);

  const handleAddToFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleAddingHotelIdToFavorites(hotel);
  };

  const filteredProducts = hotel.products.filter((p) =>
    !productCategory
      ? true
      : (Array.isArray(p.product_categories) ? p.product_categories : []).some(
          (cat) => cat?.toLowerCase() === productCategory.toLowerCase(),
        ),
  );

  const finalProducts = filteredProducts.length > 0 ? filteredProducts : hotel.products;

  const sortedProducts = useMemo(() => {
    const sortByProductType = (
      a: { product_type_sort_order: number },
      b: { product_type_sort_order: number },
    ) => a.product_type_sort_order - b.product_type_sort_order;

    const hotspotAvailable = finalProducts
      .filter((product) => product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const hotspotUnavailable = finalProducts
      .filter((product) => product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const availableNonHotspot = finalProducts
      .filter((product) => !product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const unavailableNonHotspot = finalProducts
      .filter((product) => !product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    return [
      ...hotspotAvailable,
      ...hotspotUnavailable,
      ...availableNonHotspot,
      ...unavailableNonHotspot,
    ].slice(0, 3);
  }, [finalProducts]);

  // -------------------------------------------------------------------
  // ScrollIntoView Callback -------------------------------------------
  // -------------------------------------------------------------------
  const [isVisible, currentElement] = useOnScreen();
  useEffect(() => {
    if (isVisible && onCardView) {
      onCardView(hotel);
    }
  }, [isVisible, onCardView, hotel]);

  const hotelName = useMemo(() => {
    // If no category or no filtered products, just return the hotel name
    if (!productCategory || filteredProducts.length === 0) {
      return hotel.name;
    }

    // Get the category prefix (e.g., "spa") from the lookup object
    const categoryPrefix =
      PRODUCT_TITLE_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_TITLE_CATEGORY_TEXT] ||
      '';

    // If the hotel name already starts with the prefix, no need to add it
    if (hotel.name.startsWith(categoryPrefix) || hotel.name.startsWith('Spa')) {
      return hotel.name;
    }

    // Combine the prefix and hotel name
    return `${categoryPrefix}${hotel.name}`;
  }, [hotel.name, productCategory, filteredProducts]);

  const hotelLocation = hotel.code ? `${hotel.cityName}, ${hotel.code}` : `${hotel.cityName}`;

  return (
    <>
      {showFavoriteToastMessage && <FavoritesToastMessage saveToFavorites={saveToFavorites} />}

      <button
        type="button"
        role="link"
        onClick={() => onViewAll(hotel)}
        ref={currentElement}
        className={`w-full cursor-pointer ${
          isHighlighted
            ? 'box-border rounded-none desktop:box-content static left-0 p-0 desktop:relative desktop:-left-10px desktop:p-2 bg-rp-surface-brand-daylight border-solid border-2 desktop:rounded border-rp-surface-alert-warning pb-8 desktop:pb-2'
            : ''
        }`}
      >
        <div className="flex flex-col desktop:flex-row gap-4 desktop:gap-6">
          <div className="relative">
            <Gallery
              classes="z-20 desktop:w-4/12 cursor-pointer"
              name={hotel.name}
              images={galleryImages}
              navigateToDetailsPage={() => null}
              userFavorites={userFavorites}
              handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
              hotel={hotel}
              hasHotelOffer={hotel.hasHotelOffer}
            />
          </div>

          <div className="flex flex-col gap-4 desktop:gap-6 w-full px-4 desktop:px-0">
            <div className="flex flex-col gap-1 w-full">
              <div className="flex flex-row justify-between items-center gap-1">
                <h2
                  className="text-base desktop:text-lg leading-6 font-rp-basetica-semibold text-left whitespace-nowrap overflow-hidden overflow-ellipsis desktop:max-w-md"
                  id={hotel.name}
                  title={hotelName}
                >
                  {hotelName}
                </h2>

                <div className="cursor-pointer hidden desktop:block">
                  <Image
                    src={
                      userFavorites && userFavorites.includes(hotel.id.toString())
                        ? redHeartIcon
                        : grayHeartIcon
                    }
                    alt="heart"
                    onClick={handleAddToFavorite}
                  />
                </div>
              </div>

              <div className="flex flex-row gap-1 items-center">
                {hotel.rating && hotel.rating > 0 ? (
                  <div className="flex flex-row items-center gap-1">
                    <StarRating
                      algoliaRating={hotel.rating}
                      algoliaReviews={hotel.reviewsCount}
                      reviewsThreshold={REVIEWS_THRESHOLD_TO_BE_SHOWN}
                    />
                    <div className="text-sm font-rp-basetica text-rp-text-secondary">|</div>
                  </div>
                ) : null}

                <div
                  className="text-sm font-rp-basetica text-rp-text-secondary whitespace-nowrap overflow-hidden overflow-ellipsis"
                  data-testid="hotel-city-and-state"
                  title={hotelLocation}
                >
                  {hotelLocation}
                </div>

                {vibesBadge ? (
                  <>
                    <div className="text-sm font-rp-basetica text-rp-text-secondary">|</div>
                    <div className="text-sm font-rp-basetica text-rp-text-secondary">
                      {vibesBadge}
                    </div>
                  </>
                ) : null}
              </div>

              <div className="overflow-hidden desktop:overflow-visible relative">
                <Amenities amenities={hotel.amenities} newHotelCardDesign />
              </div>
            </div>

            <Products
              hotel={hotel}
              href={href}
              onViewAll={onViewAll}
              sortedProducts={sortedProducts}
              finalProducts={finalProducts}
              filteredProducts={filteredProducts}
              searchedDate={searchedDate}
            />
          </div>
        </div>
      </button>
    </>
  );
}
